import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import FreeForm from "@widgets/FreeForm";
import { global, useMenuStore, withMenuLoading } from "@shared/libs/Global";
import SnButton from "@shared/components/SnButton";
import {
  getCodeData,
  getCurrentTime,
  getInitFormData,
} from "@shared/utils/CommonUtils";
import {
  callApi,
  fnGetDataSet,
  fnGetTableById,
  fnSave,
} from "@shared/api/restApi";
import { showIssueModal } from "@widgets/IssueModal";
import SN_SFA_ISSUE_REG_002_POP_001 from "@menu/SFA/SN_SFA_ISSUE_REG_002/SN_SFA_ISSUE_REG_002_POP_001";
import SN_SFA_ISSUE_REG_002_POP_002 from "@menu/SFA/SN_SFA_ISSUE_REG_002/SN_SFA_ISSUE_REG_002_POP_002";
import { showDialog } from "@widgets/CommonDialog";
import {
  getEmp,
  getEtcInfo,
  getProgress,
  getRelIssue,
  getTag,
} from "@shared/utils/IssueUtils";
import SN_SFA_ISSUE_REG_002_POP_003 from "@menu/SFA/SN_SFA_ISSUE_REG_002/SN_SFA_ISSUE_REG_002_POP_003";
import { PageShareState, SharePayload } from "@shared/type/ShareType";
import { sendIssueSnapshotTelegramByParams } from "../issueTelegram";
import SN_SFA_ISSUE_REG_002_POP_005 from "@menu/SFA/SN_SFA_ISSUE_REG_002/SN_SFA_ISSUE_REG_002_POP_005";
import SN_SFA_ISSUE_REG_002_DLL_BOX, {
  downloadDllFiles,
  getDllFolder,
} from "@menu/SFA/SN_SFA_ISSUE_REG_002/SN_SFA_ISSUE_REG_002_DLL_BOX";
import { UseApiService } from "@shared/hooks/useApiService";
import { showPopupModal } from "@widgets/CommonModal";

interface Props {
  Menuprops: menuInfo;
}

/**
 * 메뉴 : ISSUE MANAGEMENT
 */
const SN_SFA_ISSUE_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const { menuItems } = useMenuStore();
  const formRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [detailBody, setDetailBody] = useState<any>(null);
  const [replyList, setReplyList] = useState<any[]>([]);
  const [recipientList, setRecipientList] = useState<any[]>([]);
  const [referenceList, setReferenceList] = useState<any[]>([]);
  const [scriptList, setScriptList] = useState<any[]>([]);
  const [relIssueList, setRelIssueList] = useState<any[]>([]);
  // 모바일 요약 패널 표시용 콤보 박스 표시값 캐시
  const [displayValues, setDisplayValues] = useState<Record<string, string>>(
    {}
  );
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);
  const { apiPost } = UseApiService();
  // 제품구분(SFA043) 코드 목록: cd_flag(fg_item) → dc_ref(DB 종류. 예: MSSQL)
  const [tp_sql, setTp_sql] = useState<any[]>([]);
  // 현재 이슈의 DB 종류(MSSQL 등)와 MSSQL 이슈의 DLL 첨부 목록(AP_SFA_ISS_REG_002_POP_07_S)
  const [sqlType, setSqlType] = useState<string>("");
  const [dllList, setDllList] = useState<any[]>([]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
    onDelete,
    getShareState,
    applyShareState,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({});
        const initFormData = rtn.form.freeform1ControlData;
        setFormData((prev: any) => (prev?.no_issue ? prev : initFormData));
        setFormControlData(rtn.form.freeform1);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (Menuprops.figure) {
      onActivate(Menuprops.nodeData);
    }
  }, [Menuprops]);

  // 제품구분(SFA043) 코드 목록을 최초 1번 조회해 둔다
  useEffect(() => {
    const fetch = async () => {
      try {
        const codel = await fnGetTableById({
          id: "codel",
          params: { cd_firm: userInfo.cd_firm, cd_clases: ["SFA043"] },
        });
        setTp_sql(Array.isArray(codel) ? codel : []);
      } catch (error) {
        setTp_sql([]);
      }
    };
    fetch();
  }, []);

  // 현재 이슈의 제품구분(fg_item)에 매핑된 dc_ref(DB 종류. 예: MSSQL)
  // 드릴다운(onActivate)이 SFA043 조회 useEffect보다 먼저 돌 수 있어, 코드 목록이 비어 있으면 직접 조회한다
  const getSqlType = async (fg_item: string) => {
    let codes = tp_sql;
    if (!codes || codes.length === 0) {
      try {
        const codel = await fnGetTableById({
          id: "codel",
          params: { cd_firm: userInfo.cd_firm, cd_clases: ["SFA043"] },
        });
        codes = Array.isArray(codel) ? codel : [];
        setTp_sql(codes);
      } catch (error) {
        codes = [];
      }
    }
    const found = codes.find(
      (row: any) => String(row?.cd_flag ?? "") === String(fg_item ?? "")
    );
    return String(found?.dc_ref ?? "")
      .trim()
      .toUpperCase();
  };

  // MSSQL 이슈의 DLL 첨부 목록.
  const fetchDllList = async (no_issue: string) => {
    if (!no_issue) {
      setDllList([]);
      return;
    }
    try {
      const rtn = await callApi("executeScript/getDllList", {
        no_issue,
        latest: true,
      });
      setDllList(Array.isArray(rtn?.data) ? rtn.data : []);
    } catch (error) {
      console.error("DLL 목록 조회 실패:", error);
      setDllList([]);
    }
  };

  useEffect(() => {
    if (!formControlData || formControlData.length === 0) return;

    const resolveComboName = async (param: string, codeValue: string) => {
      if (!codeValue) return "";
      const control = (formControlData || []).find(
        (item: any) => item?.cd_param === param
      );
      if (!control) return codeValue;
      try {
        const rawCombo = (control as any).js_combo;
        // SnComboBox가 사용하는 코드 소스(js_combo/cd_clas/dc_sql) 기준으로 이름 변환
        const comboList = Array.isArray(rawCombo)
          ? rawCombo
          : typeof rawCombo === "string" && rawCombo.trim()
            ? JSON.parse(rawCombo)
            : null;
        const dataSource = comboList
          ? comboList
          : await getCodeData(
              control.cd_clas || "",
              control.dc_sql || "",
              false
            );
        const list = Array.isArray(dataSource) ? dataSource : [];
        const found = list.find(
          (row: any) => row?.code === codeValue || row?.CODE === codeValue
        );
        return found?.name ?? found?.NAME ?? codeValue;
      } catch (error) {
        return codeValue;
      }
    };

    const resolveDisplayValues = async () => {
      // 모바일 요약 패널 표기용 이름값만 계산
      const nextValues: Record<string, string> = {};
      nextValues.fg_item = await resolveComboName("fg_item", formData?.fg_item);
      nextValues.tp_issue1 = await resolveComboName(
        "tp_issue1",
        formData?.tp_issue1
      );
      nextValues.tp_issue2 = await resolveComboName(
        "tp_issue2",
        formData?.tp_issue2
      );
      nextValues.st_issue = await resolveComboName(
        "st_issue",
        formData?.st_issue
      );
      setDisplayValues(nextValues);
    };

    resolveDisplayValues();
  }, [
    formControlData,
    formData?.fg_item,
    formData?.tp_issue1,
    formData?.tp_issue2,
    formData?.st_issue,
  ]);

  const onTopBtnsClick = async (id: string) => {
    const formData = formRef.current?.getFormdata();
    if (!formData.no_issue) return;
    const st_issue = formData.st_issue;
    const raisedBy = formData.cd_emp;
    const admin = formData.cd_emp_admin;
    const recipients = formData.dc_recipient
      ? formData.dc_recipient.split(",")
      : [];
    const references = formData.dc_reference
      ? formData.dc_reference.split(",")
      : [];

    if (id === "btnOpen") {
      if (formData.dc_recipient) {
        formData.st_issue = "2";
        formData.tm_assign = getCurrentTime();
      } else {
        formData.st_issue = "1";
      }

      setFormData(formData);
      await onSave(formData);
    } else if (id == "btnStart") {
      if (st_issue === "1" || st_issue === "2") {
        // open, assign일 때
        formData.st_issue = "3";
        formData.tm_start = getCurrentTime();
        formData.cd_emp_start = userInfo.cd_emp;
      } else if (st_issue === "3") {
        // 이미 start 상태 -> 취소(open/assign)
        if (formData.dc_recipient) {
          formData.st_issue = "2";
        } else {
          formData.st_issue = "1";
        }
        formData.tm_start = null;
        formData.cd_emp_start = null;
      } else {
        await showDialog("alter", "Open/Assign 상태에서만 시작 가능합니다.");
        return;
      }

      setFormData(formData);
      await onSave(formData);
    } else if (id == "btnDone") {
      if (st_issue === "3") {
        // solving일 때
        formData.st_issue = "4";
        formData.tm_finish = getCurrentTime();
        formData.cd_emp_finish = userInfo.cd_emp;
      } else if (st_issue === "4") {
        // 이미 done 상태 -> 취소(solving)
        formData.st_issue = "3";
        formData.tm_finish = null;
        formData.cd_emp_finish = null;
      } else {
        await showDialog("alter", "Solving 상태에서만 완료 가능합니다.");
        return;
      }

      setFormData(formData);
      await onSave(formData);
      await sendMessage(formData);
    } else if (id == "btnReject") {
      if (st_issue === "4" || st_issue === "T") {
        // Done 또는 Test -> solving
        formData.st_issue = "3";
        formData.tm_reject = getCurrentTime();
        formData.cd_emp_reject = userInfo.cd_emp;
      } else {
        await showDialog("alter", "Done, Test 상태에서만 Reject 가능합니다.");
        return;
      }
      setFormData(formData);
      await onSave(formData);

      await openReplyPopup(formData);
    } else if (id == "btnConfirm") {
      if (st_issue !== "T") {
        await showDialog("alter", "Test 상태에서만 Confirm 가능합니다.");
        return;
      }

      const ok = await showDialog("confirm", "Confirm하시겠습니까?");
      if (!ok) return;

      // MSSQL 제품: 윈폼 Confirm 과 같이 미적용 스크립트를 적용 서버(pub)에서 실행한 뒤 종결한다
      if (sqlType === "MSSQL") {
        if (userInfo.cd_emp !== raisedBy && userInfo.cd_emp !== admin) {
          await showDialog("alter", "관리자나 요청자만 Confirm 가능합니다.");
          return;
        }

        // 미적용 B/P/R 스크립트 (본 DB sfa_issue.js_script 에서 읽은 scriptList)
        const pending = (scriptList ?? []).filter(
          (s: any) =>
            ["B", "P", "R"].includes(s.fg_script) &&
            s.yn_apply !== "Y" &&
            String(s.txt_script ?? "").trim() !== ""
        );
        if (pending.length > 0) {
          // Java 는 본 DB 에서 미적용 스크립트를 읽어 적용 서버(pub)에서 실행만 한다. 저장은 아래에서 본 DB 에
          const rtn = await withMenuLoading(
            Menuprops.cd_menu ?? "SN_SFA_ISSUE_REG_002",
            () =>
              callApi("executeScript/confirmScriptDotNet", {
                no_issue: formData.no_issue,
              })
          );
          // 실행 실패: 오류 메시지는 공통 처리, 상태는 바꾸지 않는다
          if (!rtn?.status) return;
        }
      }

      formData.st_issue = "5";
      formData.tm_confirm = getCurrentTime();

      setFormData(formData);
      await onSave(formData);
    } else if (id == "btnCancel") {
      formData.st_issue = "9";
      formData.tm_cancel = getCurrentTime();
      formData.cd_emp_cancel = userInfo.cd_emp;

      setFormData(formData);
      await onSave(formData);

      await openReplyPopup(formData);
    } else if (id == "btnDelete") {
      if (userInfo.cd_emp === raisedBy || userInfo.cd_emp === admin) {
        if (
          st_issue === "0" ||
          st_issue === "1" ||
          st_issue === "2" ||
          st_issue === "9"
        ) {
          await onDelete();
        } else {
          await showDialog(
            "alter",
            "Temp, Open, Assign, Cancel 상태에서만 삭제 가능합니다."
          );
        }
      } else {
        await showDialog("alter", "관리자, 등록자만 삭제 가능합니다.");
      }
    }
  };

  const onSearch = async (saveFlag?: boolean, overrideFormData?: any) => {
    const currentForm = overrideFormData ?? formData;
    if (!saveFlag) {
      const changeData = formRef.current?.getChangeData();
      if (changeData) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          await onSave();
          return;
        } else if (confirm === undefined) {
          return;
        }
      }
    }

    if (!currentForm || !currentForm.no_issue) {
      await showDialog("alter", "이슈 번호가 없습니다.");
      return;
    }

    const params = {
      id: "sn_sfa_issue_reg_002_01",
      params: { cd_firm: userInfo.cd_firm, no_issue: currentForm.no_issue },
    };
    const data: any = await fnGetDataSet(params);

    params.id = "sn_sfa_issue_reg_002_02";
    const data2: any = await fnGetDataSet(params);

    setFormData(data[0][0]);
    setDetailBody({
      txt_body: data[0][0].txt_body,
      tm_reg: data[0][0].tm_reg,
    });
    setRecipientList(data[1]);
    setRelIssueList(data[2]);
    setReplyList(data[4]);
    setReferenceList(data[5]);

    // state(formData)는 아직 갱신 전이므로 방금 조회한 행의 fg_item으로 판정한다
    // 스크립트는 제품과 상관없이 본 DB(sfa_issue.js_script)에 저장한다. MSSQL 제품은 실행만 .NET 서버에서 한다
    const sqlType = await getSqlType(data[0][0]?.fg_item);
    setSqlType(sqlType);
    setScriptList(data2[0]);
    if (sqlType === "MSSQL") {
      await fetchDllList(currentForm.no_issue);
    } else {
      setDllList([]);
    }
  };

  const onSave = async (changedFormData?: any) => {
    const formData = changedFormData || formRef.current?.getFormdata();

    const updateData: any = {
      cd_firm: userInfo.cd_firm,
      no_issue: formData.no_issue,
      cd_user_amd: userInfo.cd_user,
      tm_amd: formData.tm_amd,
      dc_title: formData.dc_title,
      fg_item: formData.fg_item,
      st_issue: formData.st_issue,
    };

    // js 데이터 추가
    const js_progress = getProgress(formData);
    const js_etc_info = getEtcInfo(formData);
    const js_rel = getRelIssue(formData);
    const js_tag = getTag(formData);
    const js_emp = getEmp(formData);

    if (js_progress) updateData.js_progress = js_progress;
    if (js_etc_info) updateData.js_etc_info = js_etc_info;
    if (js_rel) updateData.js_rel = js_rel;
    if (js_tag) updateData.js_tag = js_tag;
    if (js_emp) updateData.js_emp = js_emp;

    const dtDue = formData.dt_due;
    updateData.dt_due = dtDue
      ? `${dtDue.substring(0, 4)}-${dtDue.substring(4, 6)}-${dtDue.substring(6, 8)} 00:00:00.000`
      : null;
    formData.tp_issue1
      ? (updateData.tp_issue1 = formData.tp_issue1)
      : (updateData.tp_issue1 = null);
    formData.tp_issue2
      ? (updateData.tp_issue2 = formData.tp_issue2)
      : (updateData.tp_issue2 = null);
    formData.fg_priority
      ? (updateData.fg_priority = formData.fg_priority)
      : (updateData.fg_priority = null);
    formData.tm_finish
      ? (updateData.tm_done = formData.tm_finish)
      : (updateData.tm_done = null);
    if (formData.tm_confirm) updateData.tm_confirm = formData.tm_confirm;
    if (formData.dc_recipient) {
      updateData.arr_cd_emp_assign = formData.dc_recipient
        ? formData.dc_recipient
            .split(",")
            .map((v: string) => v.trim())
            .filter((v: string) => v !== "")
        : [];
    } else {
      updateData.arr_cd_emp_assign = null;
    }

    const saveData = {
      sfa_issue: {
        update: [updateData],
      },
    };

    await fnSave("SN_SFA_ISSUE_REP_001_btnNewIssue", saveData);
    await onSearch(true);
    return true;
  };

  const onDelete = async () => {
    let formData = formRef.current?.getFormdata();
    formData.cd_firm = userInfo.cd_firm;
    formData.dt_due = null;

    const saveData = {
      sfa_issue: {
        delete: [formData],
      },
    };

    try {
      await fnSave("SN_SFA_ISSUE_REP_001_btnNewIssue", saveData);
      formData.no_issue = "";
      setDetailBody("");
      setReplyList([]);
      await onSearch(true);
    } catch (err) {}
  };

  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const params = {
        id: "sn_sfa_issue_reg_002_01",
        params: { cd_firm: userInfo.cd_firm, no_issue: nodeData.no_issue },
      };
      const data: any = await fnGetDataSet(params);

      params.id = "sn_sfa_issue_reg_002_02";
      const data2: any = await fnGetDataSet(params);

      setFormData(data[0][0]);
      setDetailBody({
        txt_body: data[0][0].txt_body,
        tm_reg: data[0][0].tm_reg,
      });
      setRecipientList(data[1]);
      setRelIssueList(data[2]);
      setReplyList(data[4]);
      setReferenceList(data[5]);

      // state(formData)는 아직 갱신 전이므로 방금 조회한 행의 fg_item으로 판정한다
      // 스크립트는 제품과 상관없이 본 DB(sfa_issue.js_script)에 저장한다
      const sqlType = await getSqlType(data[0][0]?.fg_item);
      setSqlType(sqlType);
      setScriptList(data2[0]);
      if (sqlType === "MSSQL") {
        await fetchDllList(data[0][0].no_issue);
      } else {
        setDllList([]);
      }
    }
  };

  const isRaised = () => {
    try {
      return formData.cd_emp === userInfo.cd_emp;
    } catch (e) {
      return false;
    }
  };

  const isUpToFinish = () => {
    const status = ["0", "1", "2", "3"];
    const st_issue = formData.st_issue;

    try {
      if (!status.includes(st_issue)) {
        return true;
      }
    } catch (error) {
      return false;
    }
    return false;
  };

  const onBottomBtnsClick = async (id: string) => {
    // 팝업 열기 전,
    await onBeforeOpenPopup();
    if (!formData) return;

    if (id === "modify") {
      if (!isRaised() || isUpToFinish()) return;

      const component = (
        <SN_SFA_ISSUE_REG_002_POP_001
          formData={formData}
          Menuprops={Menuprops}
          txt_body={detailBody.txt_body}
        />
      );
      const result = await showIssueModal({
        title: "Modify",
        children: component,
      });
      if (result) {
        await onSearch(true);
      }
    } else if (id === "reply") {
      // st_issue가 9이면 중지
      const st_issue = formData.st_issue;
      if (st_issue === "9") return;

      const component = (
        <SN_SFA_ISSUE_REG_002_POP_002
          formData={formData}
          replyList={replyList}
          // files={fileList}
        />
      );
      const result = await showIssueModal({
        title: "Reply",
        children: component,
      });
      if (result) {
        await onSearch(true);
      }
    } else if (id === "list") {
      const cd_menu = "SN_SFA_ISSUE_REP_001";
      if (Menuprops) {
        const filePath =
          `./${Menuprops.cd_module}/${cd_menu}/${cd_menu}`.toUpperCase();
        const drillDown = menuItems
          ? menuItems.find((item: any) => item?.cd_menu === cd_menu)
          : undefined;
        if (drillDown) {
          (window as any).onActivate(
            drillDown.cd_menu, // 메뉴 키
            drillDown.nm_menu, // 메뉴 제목
            drillDown.cd_module, // 모듈
            filePath, // 파일 경로
            drillDown, // 데이터
            drillDown.nm_path, // 파일경로 한글화
            {},
            "fg_drilldown.Search"
          );
        }
      }
    } else if (id === "design") {
      const current = formRef.current?.getFormdata?.() ?? formData ?? {};
      // 이슈번호는 메뉴디자이너가 배포할 화면의 cd_menu가 된다.
      // 없는 채로 탭을 열면 화면은 만들어도 배포 단계에서 막히므로 진입 자체를 안 시킨다.
      if (!String(current.no_issue ?? "").trim()) {
        await showDialog(
          "alter",
          "이슈번호가 없습니다. 이슈를 저장한 뒤 DESIGN을 실행해 주세요."
        );
        return;
      }
      // 2026-09-29 메뉴디자이너 재설계 — 새 화면(007)으로. 옛 006(aiStudio) 은 건드리지 않는다
      const cd_menu = "SN_SFA_ISSUE_REG_007";
      if (Menuprops) {
        const filePath =
          `./${Menuprops.cd_module}/${cd_menu}/${cd_menu}`.toUpperCase();
        const drillDown = menuItems
          ? menuItems.find((item: any) => item?.cd_menu === cd_menu)
          : undefined;
        if (drillDown) {
          (window as any).onActivate(
            drillDown.cd_menu, // 메뉴 키
            drillDown.nm_menu, // 메뉴 제목
            drillDown.cd_module, // 모듈
            filePath, // 파일 경로
            drillDown, // 데이터
            drillDown.nm_path, // 파일경로 한글화
            {
              no_issue: current.no_issue,
              cd_menu: current.cd_menu,
              cd_emp_admin: current.cd_emp_admin,
            },
            "fg_design.Open"
          );
        }
      }
    }
  };

  const onFormBtnClick = async (id: string) => {
    if (id === "btn_script") {
      const sqlType = await getSqlType(formData?.fg_item);
      const component = (
        <SN_SFA_ISSUE_REG_002_POP_003
          formData={formData}
          Menuprops={Menuprops}
          scriptList={scriptList}
          sqlType={sqlType}
        />
      );
      const result = await showIssueModal({
        title: "DataBase Script Management",
        children: component,
      });
      if (result) {
        await onSearch(true);
      }
    } else if (id === "btn_test") {
      if (!formData?.no_issue) {
        await showDialog("alter", "이슈번호가 없습니다.");
        return;
      }
      const st_issue = formData.st_issue;

      if (st_issue !== "4" && st_issue !== "T") {
        await showDialog("alter", "Done 상태에서만 Test 가능합니다.");
        return;
      }

      // MSSQL 제품: 윈폼 Apply Test 와 동일. DLL 첨부를 내려받고 상태만 T 로 바꾼다 (스크립트는 적용하지 않음)
      if (sqlType === "MSSQL") {
        const hasDll = dllList.length > 0;

        if (hasDll) {
          const ok = await withMenuLoading(
            Menuprops.cd_menu ?? "SN_SFA_ISSUE_REG_002",
            () =>
              downloadDllFiles(
                formData.no_issue,
                formData.fg_item,
                dllList.map((d: any) => d.nm_file)
              )
          );
          if (!ok) return;
        }

        formData.st_issue = "T";
        formData.tm_test = getCurrentTime();
        setFormData(formData);
        await onSave(formData);
        return;
      }

      const result = await showPopupModal({
        title: "메뉴 동기화",
        width: "30%",
        height: "30%",
        okayBtn: true,
        validation: true,
        children: <SN_SFA_ISSUE_REG_002_POP_005 />,
      });
      if (!result?.serverCode) return;

      const response = await withMenuLoading(
        Menuprops.cd_menu ?? "SN_SFA_ISSUE_REG_002",
        () =>
          apiPost("/upgrade/issue-apply", {
            noIssue: formData.no_issue,
            url: result.serverCode,
          })
      );
      // 동기화 실패 시 공통 에러창만 띄우고 상태는 저장하지 않는다
      if (response.status !== "S") return;

      formData.st_issue = "T";
      formData.tm_test = getCurrentTime();
      setFormData(formData);
      await onSave(formData);
    } else if (id === "btn_publish") {
      if (!formData?.no_issue) return;

      const raisedBy = formData.cd_emp;
      const admin = formData.cd_emp_admin;

      if (userInfo.cd_emp !== raisedBy && userInfo.cd_emp !== admin) {
        await showDialog("alter", "관리자나 요청자만 배포 가능합니다.");
        return;
      }

      // MSSQL 제품: 윈폼 Apply Publish. Confirm(5) 된 건의 DLL 첨부를 배포 서버(upgrade)로 올린다. 스크립트는 Confirm 에서 이미 반영됨
      if (sqlType === "MSSQL") {
        if (formData.st_issue !== "5") {
          await showDialog("alter", "Confirm된 건만 배포 가능 합니다.");
          return;
        }
        if (dllList.length === 0) {
          await showDialog(
            "alter",
            "적용할 내용이(dll, repx, xml..) 없습니다."
          );
          return;
        }

        const ok = await showDialog(
          "confirm",
          `DLL ${dllList.length}건을 배포 서버로 올리시겠습니까?`
        );
        if (!ok) return;

        // 결과 메시지(업로드/제외 목록)는 callApi 가 응답 type/message 로 띄운다
        await withMenuLoading(Menuprops.cd_menu ?? "SN_SFA_ISSUE_REG_002", () =>
          callApi("executeScript/publishDllDotNet", {
            no_issue: formData.no_issue,
            fg_item: formData.fg_item,
            dll_folder: getDllFolder(formData.fg_item),
            files: dllList.map((d: any) => d.nm_file),
          })
        );
        return;
      }
    }
  };

  const onBeforeOpenPopup = async () => {
    const formData = formRef.current?.getFormdata();
    const updateData: any = {
      cd_firm: userInfo.cd_firm,
      no_issue: formData.no_issue,
      cd_user_amd: userInfo.cd_user,
      tm_amd: formData.tm_amd,
      dc_title: formData.dc_title,
      fg_item: formData.fg_item,
      st_issue: formData.st_issue,
    };

    // 2. js 데이터 추가
    const js_progress = getProgress(formData);
    const js_etc_info = getEtcInfo(formData);
    const js_rel = getRelIssue(formData);
    const js_tag = getTag(formData);
    const js_emp = getEmp(formData);

    if (js_progress) updateData.js_progress = js_progress;
    if (js_etc_info) updateData.js_etc_info = js_etc_info;
    if (js_rel) updateData.js_rel = js_rel;
    if (js_tag) updateData.js_tag = js_tag;
    if (js_emp) updateData.js_emp = js_emp;

    const saveData = {
      sfa_issue: {
        update: [updateData],
      },
    };

    const result = await callApi("SN_SFA_ISSUE_REP_001_btnNewIssue", saveData);
    if (result.status) {
      await onSearch(true);
    } else {
      await showDialog("error", "파일 업로드 중 오류가 발생했습니다.");
    }
  };

  const downloadFile = async (e: any) => {
    const target = e.target as HTMLElement;

    if (target.closest(".file-widget-download")) {
      const widgetEl = target.closest(".file-widget") as HTMLElement;
      const fileUrl = widgetEl.dataset.fileUrl;
      const fileName = widgetEl.dataset.fileName;

      if (!fileUrl) return;

      try {
        const params = {
          dc_url: fileUrl,
        };
        const res = await fetch("/snotes/file/download2", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
          body: JSON.stringify(params),
        });
        if (!res.ok) throw new Error("다운로드 실패");

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "download";
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        alert("파일 다운로드 실패");
      }
    }
  };

  const onValueChanged = (data: any, param: string, formData: any) => {
    switch (param) {
      case "cd_menu":
        return {
          nm_menu: data.nm_menu,
          cd_menu: data.cd_menu,
          nm_emp_design: data.nm_emp_design,
          nm_emp_dev: data.nm_emp_dev,
        };
    }
  };

  const sendMessage = async (data: any) => {
    // 링크 생성은 우선순위: 현재 폼데이터 -> 전달된 data
    const params = {
      ...formData,
      ...data,
      ...{ cd_firm: userInfo.cd_firm, msg_flag: "reply" },
    };
    // 2026-03-09: const result = callApi("telegram/sendMsg", params);
    await sendIssueSnapshotTelegramByParams(params);
  };

  const openReplyPopup = async (formData: any) => {
    if (!formData) return;

    const component = (
      <SN_SFA_ISSUE_REG_002_POP_002 formData={formData} replyList={replyList} />
    );
    const result = await showIssueModal({
      title: "Reply",
      children: component,
    });
    if (result) {
      await onSearch(true);
    }
  };

  // 공유 버튼에서 호출: 현재 이슈 번호와 제목 중심의 조회/파라미터 상태를 넘겨준다.
  const getShareState = (): PageShareState => {
    const current = formRef.current?.getFormdata?.() ?? formData ?? {};
    return {
      search: {
        no_issue: current.no_issue ?? "",
        st_issue: current.st_issue ?? "",
        dc_title: current.dc_title ?? "",
      },
      params: {
        no_issue: current.no_issue ?? "",
      },
    };
  };

  // 공유 링크 복원 시 호출: 전달받은 상태를 폼에 반영하고 자동 조회까지 수행한다.
  const applyShareState = async (
    state: PageShareState,
    payload?: SharePayload
  ) => {
    const nextForm = {
      ...formData,
      ...(state.search || {}),
      ...(state.params || {}),
    };
    setFormData(nextForm);

    // state 반영 후 조회 재실행 (no_issue 기준)
    if (state.params?.no_issue || state.search?.no_issue) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      await onSearch(true, nextForm);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (
      trimmed.length >= 10 &&
      (trimmed.includes("-") || trimmed.includes("/"))
    ) {
      return trimmed.slice(0, 10).replace(/-/g, "/");
    }
    if (/^\d{8}$/.test(trimmed)) {
      return `${trimmed.slice(0, 4)}/${trimmed.slice(4, 6)}/${trimmed.slice(6, 8)}`;
    }
    return trimmed;
  };

  const formatIssueType = () => {
    // 제품구분/유형1/유형2를 이름 우선으로 결합
    return [
      formData?.nm_fg_item || displayValues.fg_item || formData?.fg_item,
      formData?.nm_tp_issue1 || displayValues.tp_issue1 || formData?.tp_issue1,
      formData?.nm_tp_issue2 || displayValues.tp_issue2 || formData?.tp_issue2,
    ]
      .filter(Boolean)
      .join(" / ");
  };

  const formatFiles = (files: any) => {
    // 첨부파일 표시 문자열 구성
    if (!files) return "";
    if (Array.isArray(files)) {
      return files
        .map((file) => file?.nm_file ?? file?.name ?? String(file))
        .join(", ");
    }
    return typeof files === "string" ? files : String(files);
  };

  const getEmpNameByCode = (list: any[], code: string) => {
    // 텔레그램 공유 요약 패널에서 담당자/참조자 이름 매핑
    const codeKeyCandidates = [
      "cd_emp",
      "cd_user",
      "cd_emp_recipient",
      "cd_emp_ref",
    ];
    const nameKeyCandidates = [
      "nm_emp",
      "nm_user",
      "nm_emp_recipient",
      "nm_emp_ref",
    ];
    const row = list.find((item) =>
      codeKeyCandidates.some((key) => item?.[key] === code)
    );
    if (!row) return "";
    const nameKey = nameKeyCandidates.find((key) => row?.[key]);
    return nameKey ? row[nameKey] : "";
  };

  const formatEmpList = (codes: string | undefined, list: any[]) => {
    // 콤마로 구분된 사번 목록을 이름 목록으로 변환
    if (!codes) return "";
    const items = String(codes)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    return items.map((code) => getEmpNameByCode(list, code) || code).join(", ");
  };

  const formatMenuLabel = () => {
    // 메뉴코드(메뉴명) 형태로 표시
    const code = formData?.cd_menu;
    if (!code) return "";
    const menu = menuItems?.find((item: any) => item?.cd_menu === code);
    const name = menu?.nm_menu ?? formData?.nm_menu ?? "";
    return name ? `${code}(${name})` : code;
  };

  const summaryRows = [
    { key: "issueNo", label: "이슈번호", value: formData?.no_issue },
    { key: "title", label: "제목", value: formData?.dc_title },
    {
      key: "status",
      label: "상태",
      value: displayValues.st_issue || formData?.st_issue,
    },
  ];

  const displayRows = [
    { label: "이슈번호", value: formData?.no_issue },
    { label: "제목", value: formData?.dc_title },
    { label: "구분", value: formatIssueType() },
    { label: "거래처", value: formData?.nm_cust },
    { label: "등록자", value: formData?.nm_emp },
    { label: "관리자", value: formData?.nm_emp_admin },
    {
      label: "담당자",
      value: formatEmpList(formData?.dc_recipient, recipientList),
    },
    {
      label: "참조자",
      value: formatEmpList(formData?.dc_reference, referenceList),
    },
    { label: "관련이슈", value: formData?.no_issue_rel },
    { label: "등록일", value: formatDate(formData?.dt_issue) },
    { label: "마감일", value: formatDate(formData?.dt_due) },
    { label: "상태", value: displayValues.st_issue || formData?.st_issue },
    { label: "메뉴", value: formatMenuLabel() },
    { label: "첨부파일", value: formatFiles(formData?.files) },
  ];

  return (
    <div
      className="overflow-y-auto md:overflow-hidden"
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
      }}
    >
      <div
        // 모바일에서는 요약 패널만 노출
        className="hidden md:block"
        style={{
          flexShrink: 0,
        }}
      >
        <FreeForm
          ref={formRef}
          data={formData}
          controls={formControlData}
          autoFocus={false}
          title={"이슈 관리"}
          header={
            <div>
              <SnButton
                text={"Design"}
                onClick={() => {
                  onBottomBtnsClick("design");
                }}
              />
              <SnButton
                text={"Modify"}
                onClick={() => {
                  onBottomBtnsClick("modify");
                }}
              />
              <SnButton
                text={"Reply"}
                onClick={() => {
                  onBottomBtnsClick("reply");
                }}
              />
              <SnButton
                text={"List"}
                onClick={() => {
                  onBottomBtnsClick("list");
                }}
              />
              <span style={{ marginRight: "30px" }}></span>
              {formData?.st_issue === "0" && (
                <SnButton
                  text={"Open"}
                  onClick={() => onTopBtnsClick("btnOpen")}
                />
              )}
              <SnButton
                text={formData?.st_issue === "3" ? "Start 취소" : "Start"}
                onClick={() => {
                  onTopBtnsClick("btnStart");
                }}
              />
              <SnButton
                text={formData?.st_issue === "4" ? "Done 취소" : "Done"}
                onClick={() => {
                  onTopBtnsClick("btnDone");
                }}
              />
              <SnButton
                text={"Reject"}
                onClick={() => {
                  onTopBtnsClick("btnReject");
                }}
              />
              <SnButton
                text={"Confirm"}
                onClick={() => {
                  onTopBtnsClick("btnConfirm");
                }}
              />
              <SnButton
                text={"Cancel"}
                onClick={() => {
                  onTopBtnsClick("btnCancel");
                }}
              />
              <SnButton
                text={"Delete"}
                onClick={() => {
                  onTopBtnsClick("btnDelete");
                }}
              />
            </div>
          }
          onButtonClick={onFormBtnClick}
          onValueChanged={onValueChanged}
        />
        {/* MSSQL 제품 이슈만 DLL/XML 첨부 박스 표시 (윈폼 POP_03 DLL 박스 대응) */}
        {sqlType === "MSSQL" && formData?.no_issue && (
          <SN_SFA_ISSUE_REG_002_DLL_BOX
            no_issue={formData.no_issue}
            fg_item={formData.fg_item}
            dllList={dllList}
            st_issue={formData.st_issue}
            onUploaded={() => fetchDllList(formData.no_issue)}
            onDeleted={() => fetchDllList(formData.no_issue)}
          />
        )}
      </div>
      {/* 모바일 요약 패널 */}
      <div className="md:hidden border border-[rgb(186,191,199)] rounded-md p-3 space-y-2 bg-white">
        {summaryRows.map((row) => (
          <div
            key={row.label}
            className="text-[13px] leading-5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center">
              <span className="font-semibold text-slate-700">{row.label}</span>
              <span className="mx-2 text-slate-400">|</span>
              <span className="text-slate-900">{row.value ?? ""}</span>
            </div>
            {row.key === "issueNo" && formData?.no_issue && (
              <SnButton
                text={"Reply"}
                onClick={() => onBottomBtnsClick("reply")}
              />
            )}
          </div>
        ))}
        <div className="pt-1">
          <button
            type="button"
            className="text-[12px] text-blue-600"
            onClick={() => setIsMobileSummaryOpen((prev) => !prev)}
          >
            {isMobileSummaryOpen ? "상세 접기" : "상세 펼치기"}
          </button>
        </div>
      </div>
      {isMobileSummaryOpen && (
        <div className="md:hidden border border-[rgb(186,191,199)] rounded-md p-3 space-y-2 bg-white">
          {displayRows.map((row) => (
            <div key={row.label} className="text-[13px] leading-5">
              <span className="font-semibold text-slate-700">{row.label}</span>
              <span className="mx-2 text-slate-400">|</span>
              <span className="text-slate-900">{row.value ?? ""}</span>
            </div>
          ))}
        </div>
      )}

      {/* 본문 영역 */}
      <div
        className="overflow-x-auto md:overflow-x-hidden overflow-y-visible md:overflow-y-hidden"
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          marginTop: 10,
        }}
      >
        <div
          className="md:overflow-y-auto overflow-y-visible"
          style={{
            flex: 1,
            border: "1px solid rgb(186,191,199)",
            backgroundColor: "white",
          }}
        >
          {/* 본문 */}
          <div
            style={{
              padding: "16px",
              borderBottom: "1px solid #eee",
              justifyContent: "space-between",
              fontSize: "14px",
              lineHeight: "1.6",
            }}
          >
            <div style={{ display: "flex", gap: "6px", flex: 1 }}>
              <div style={{ fontWeight: "bold" }}>•</div>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                dangerouslySetInnerHTML={{
                  __html: detailBody ? detailBody.txt_body : "",
                }}
                onClick={downloadFile}
              ></div>
            </div>
            <div
              style={{
                textAlign: "right",
                fontSize: "12px",
                marginTop: "8px",
              }}
            >
              {detailBody ? detailBody.tm_reg : ""}
            </div>
          </div>

          {/* 댓글 리스트 */}
          <div>
            {Array.isArray(replyList) &&
              replyList.map((reply, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "16px",
                    borderBottom: "1px solid #eee",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  <div
                    style={{
                      marginBottom: "4px",
                    }}
                  >
                    • {`${reply.nm_emp ? reply.nm_emp : ""}` + ` >`}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      paddingLeft: "8px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: reply ? reply.txt_body : "",
                    }}
                    onClick={downloadFile}
                  />
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: "12px",
                      marginTop: "8px",
                    }}
                  >
                    {reply?.tm_reg}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
});

export default SN_SFA_ISSUE_REG_002;
